import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLoadingStore, selectLoading } from '@/modules/shared/stores/loadingStore'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { LoaderIcon } from '@/app/features/icons/Loader'
import CompanyRow from './CompanyRow'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableEmpty from '@/modules/drivers/shared/tableItems/TableEmpty'
import { useTable } from '@/modules/drivers/context/TableContext'
import { getCompaniesTableColumns } from '../constants/tableColumns'
import { useQueryCompanies } from '@/modules/shared/query-client/useQueryCompanies'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { CompanyDetailsModel } from '@/app/models/companyDetailsModel'
import { CompanyType } from '@/app/constants/companyType'
import { CompanyGroupModel } from '../types/companyGroups'
import { getCompaniesInGroup } from '@/lib/api/company/configuration/adminPanel/companies/groups/getCompaniesInGroup'
import { useQueryCompanyDatabaseSizesBulk } from '@/lib/api/company/useCompanyDatabaseHooks'

interface CompaniesTableProps {
  companyId: string
  search?: string
  selectedGroupFilter?: CompanyGroupModel | null
  /** When provided, the table only includes companies whose id is in this set. */
  companyIdsAllowlist?: ReadonlySet<string>
  /** Row click handler — replaces the default toggle-selection on row click. */
  onRowClick?: (company: CompanyDetailsModel) => void
  /** Optional per-row edit handler — when provided, renders a pencil icon on each row. */
  onEdit?: (company: CompanyDetailsModel) => void
}

const CompaniesTable: React.FC<CompaniesTableProps> = ({
  companyId,
  search = '',
  selectedGroupFilter = null,
  companyIdsAllowlist,
  onRowClick,
  onEdit,
}) => {
  const { t } = useLocalize()
  const CompaniesTableColumns = React.useMemo(() => getCompaniesTableColumns(t), [t])
  const loading = useLoadingStore(selectLoading)
  const { data: companies = [], isLoading } = useQueryCompanies({
    includeDeleted: false,
  })
  const {
    toggleAll,
    toggleId,
    selectedGroup,
    setAllIds,
    isSelectedAny,
  } = useTable()

  const customGroupId = selectedGroupFilter && selectedGroupFilter.id > 0 ? selectedGroupFilter.id : null

  const { data: groupCompanyIds } = useQuery({
    queryKey: ['companiesInGroup', companyId, customGroupId],
    queryFn: async () => {
      const res = await getCompaniesInGroup({ companyId, groupId: customGroupId! })
      return res.data
    },
    enabled: customGroupId !== null,
  })

  // Filter companies based on search, deleted status, group filter, and optional allowlist
  const filteredCompanies = React.useMemo(() => {
    // Service companies (konta usługowe) are billing/umbrella containers, not
    // companies you administer here, so they never belong in this list.
    let filtered = companies.filter(
      (company) => company.deletedDate == null && company.type !== CompanyType.SERVICE,
    )

    if (companyIdsAllowlist) {
      filtered = filtered.filter((company) => companyIdsAllowlist.has(company.id))
    }

    // Apply group filter
    if (selectedGroupFilter) {
      if (selectedGroupFilter.id === -1) {
        filtered = filtered.filter((company) => company.isActive === true)
      } else if (selectedGroupFilter.id === -2) {
        filtered = filtered.filter((company) => company.isActive === false)
      } else if (selectedGroupFilter.id > 0 && groupCompanyIds) {
        filtered = filtered.filter((company) => groupCompanyIds.includes(company.id))
      }
    }

    if (search) {
      const searchLower = search.toLowerCase()
      filtered = filtered.filter(
        (company) =>
          company.companyName?.toLowerCase().includes(searchLower) ||
          company.uniqueIdentifier?.toLowerCase().includes(searchLower),
      )
    }

    return filtered
  }, [companies, search, selectedGroupFilter, groupCompanyIds, companyIdsAllowlist])

  React.useEffect(() => {
    setAllIds(
      filteredCompanies.map((company) => ({
        id: company.id,
      })),
    )
  }, [filteredCompanies, setAllIds])

  // Fetch all DB sizes in a single bulk request instead of one request per row.
  const { data: bulkSizes, isLoading: isSizesLoading } =
    useQueryCompanyDatabaseSizesBulk(filteredCompanies.map((c) => c.id))

  // Aggregate each company's databases into a single total so the row gets a
  // ready-to-render number.
  const sizeByCompanyId = React.useMemo(() => {
    const map = new Map<string, number | null>()
    bulkSizes?.forEach((entry) => {
      const sizes = entry.databases
        .map((db) => db.sizeBytes)
        .filter((v): v is number => v != null)
      map.set(entry.companyId, sizes.length > 0 ? sizes.reduce((a, b) => a + b, 0) : null)
    })
    return map
  }, [bulkSizes])

  const { sortedData, sortConfig, handleHeaderClick } = useSortableData({
    data: filteredCompanies,
    tableColumns: CompaniesTableColumns,
  })

  const isLoadingData = loading || isLoading

  return (
    <section className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col ">
      {isLoadingData ? (
        <div className="tw-grid tw-place-items-center">
          <LoaderIcon className="tw-w-16 tw-animate-spin" />
        </div>
      ) : sortedData.length > 0 ? (
        <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto">
          <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
            <TableHeader
              opacity
              rounded={false}
              toggleAll={toggleAll}
              isSelectedAny={isSelectedAny}
              columns={CompaniesTableColumns}
              sortConfig={sortConfig}
              sticky
              onHeaderClick={handleHeaderClick}
            />
            <tbody className="">
              {sortedData.map((company: CompanyDetailsModel) => (
                <CompanyRow
                  key={company.id}
                  company={company}
                  selectedIds={selectedGroup}
                  toggleId={toggleId}
                  isSelectedAny={isSelectedAny}
                  search={search}
                  onClick={onRowClick ? () => onRowClick(company) : undefined}
                  onEdit={onEdit}
                  totalSize={sizeByCompanyId.get(company.id) ?? null}
                  isSizeLoading={isSizesLoading}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <TableHeader
            opacity
            toggleAll={toggleAll}
            isSelectedAny={isSelectedAny}
            columns={CompaniesTableColumns}
            sortConfig={sortConfig}
            onHeaderClick={handleHeaderClick}
          />
          <TableEmpty
            title={!search ? t('addCompany.noCompanies') : t('addCompany.noSearchResults')}
          />
        </>
      )}
    </section>
  )
}

export default CompaniesTable

